// A solution's catalogue entry (02-DESIGN/14-solutions/01-the-manifest.md §
// install): its declaration from the catalogue with the one primary action
// *Install*, which is the consent, followed by the account-local steps.
// Drawn by a solution's page while it is not installed, and by Audit & usage
// for the observability solution (hq decision 0048). A licensed solution
// says *requires a license* with its publisher's hint, and *Install* asks
// for the license in a field before the consent, in the same form; it rides
// in the install's body (02-DESIGN/17-plans/03-solution-licenses.md).

import { useState, type ReactNode } from "react";

import { isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  TextArea,
  useLoad,
} from "../../ui/components.tsx";
import { Subjects as AuthSubjects, type TenantReply } from "../../wire/auth.ts";
import { SERVICE as AUTH } from "../../wire/identity.ts";
import {
  Code,
  SERVICE,
  Subjects,
  type InstallRequest,
  type Publication,
  type Record_,
  type RevisionRequest,
} from "../../wire/solutions.ts";
import { PlanRefused, usePlan } from "../settings/PlanNotes.tsx";
import { useSurface } from "../surface.tsx";
import { Declaration } from "./Declaration.tsx";
import { readInstalled } from "./installed.ts";
import { PageRedirectNote } from "./PageRedirectNote.tsx";
import { runSteps } from "./install.ts";
import { hintSentence, licenseRefusal, REQUIRES } from "./license.ts";

/** useWorkspace is what an admin reads of the relation: solo or managed, and who manages it. */
export function useWorkspace(admin: boolean) {
  const s = useSurface();
  const [t] = useLoad(`solutions/workspace/${s.tenant}/${String(admin)}`, () =>
    admin
      ? s.bus.call<TenantReply>(AUTH, AuthSubjects.tenantGet(), undefined)
      : Promise.resolve<TenantReply | undefined>(undefined),
  );
  const tenant = t.state === "ready" ? t.value?.tenant : undefined;
  return {
    ready: t.state !== "pending",
    solo: tenant?.managed !== true,
    parent: tenant?.parent?.name ?? tenant?.parent?.slug,
  };
}

/**
 * NotInstalled is a solution's catalogue entry: its declaration, and for an
 * admin *Install*, which is the consent. Audit & usage draws the observability
 * solution's here under its own title while it is not installed (hq decision 0048).
 */
export function NotInstalled({
  name,
  crumb,
  title,
  onInstalled,
}: {
  name: string;
  /** The crumb, or how to draw it once the declaration names the solution's display name. */
  crumb?: ReactNode | ((display: string) => ReactNode);
  /** The page's title where it is not the solution's own name. */
  title?: string;
  onInstalled: (err?: unknown) => void;
}) {
  const s = useSurface();
  const { platform } = usePage();
  const admin = s.offered("solutions:manage");
  const ws = useWorkspace(admin);
  const plan = usePlan();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [license, setLicense] = useState("");
  const [pub] = useLoad(`solution/catalogue/${name}`, () =>
    s.bus.call<Publication>(SERVICE, Subjects.catalogueGet(name), {} satisfies RevisionRequest),
  );
  const crumbFor = (display: string): ReactNode => (typeof crumb === "function" ? crumb(display) : crumb);
  if (pub.state === "pending") {
    return (
      <>
        <div className="crumb">{crumbFor(name)}</div>
        <Pending what="Reading its declaration" />
      </>
    );
  }
  if (pub.state === "refused") {
    return (
      <>
        <PageHead crumb={crumbFor(name)} title={title ?? name} line="Not installed here." />
        {isRefusal(pub.err, Code.notFound) ? (
          <Empty>There is no solution of that name that this workspace may install.</Empty>
        ) : (
          <NotAllowed err={pub.err} what="Its declaration" />
        )}
      </>
    );
  }
  const p = pub.value;
  const licensing = p.manifest.license;
  const token = license.trim();
  const install = () => {
    if (licensing !== undefined && token === "") {
      return;
    }
    setBusy(true);
    setErr(undefined);
    s.bus
      .call<Record_>(
        SERVICE,
        Subjects.install(name),
        (licensing !== undefined
          ? { revision: p.revision, license: token }
          : { revision: p.revision }) satisfies InstallRequest,
        60000,
      )
      .then((rec) =>
        runSteps(s.bus, rec.manifest ?? p.manifest, { solo: ws.solo, images: platform.images }).then(() =>
          readInstalled(s.bus),
        ),
      )
      .then(
        () => {
          setBusy(false);
          onInstalled();
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
          onInstalled(e);
        },
      );
  };
  return (
    <>
      <PageHead
        crumb={crumbFor(p.manifest.display)}
        title={title ?? p.manifest.display}
        line={p.manifest.description ?? "Not installed here."}
        action={
          admin ? (
            <Button
              kind="primary"
              onClick={install}
              disabled={busy || !ws.ready || (licensing !== undefined && token === "")}
            >
              {busy ? "Installing…" : "Install"}
            </Button>
          ) : undefined
        }
      />
      {err !== undefined &&
        (licenseRefusal(err, p.manifest.display, licensing?.hint) !== undefined ? (
          <Notice kind="bad" title="Install: refused">
            {licenseRefusal(err, p.manifest.display, licensing?.hint)}
          </Notice>
        ) : (
          <PlanRefused err={err} holder={plan.holder} />
        ))}
      {licensing !== undefined && (
        <Notice title={`${p.manifest.display} ${REQUIRES}`}>{hintSentence(licensing.hint)}</Notice>
      )}
      {licensing !== undefined && admin && (
        <div className="card section">
          <Field label="License" hint={`The license ${p.manifest.display}'s publisher issued, pasted whole.`}>
            <TextArea value={license} onChange={setLicense} rows={3} mono disabled={busy} />
          </Field>
        </div>
      )}
      {admin && (
        <Notice title="What this solution may do">
          Installing it is your consent to what follows, for {s.name}. It can do nothing else, whatever its
          code does.
        </Notice>
      )}
      {admin && <PageRedirectNote m={p.manifest} workspace={s.name} />}
      <Declaration m={p.manifest} from={{ publisher: p.publisher, revision: p.revision, images: p.images }} />
    </>
  );
}
